import type { ReactNode } from 'react';
import styles from './UnderstandingPanel.module.css';
import { Icon } from './Icon.js';
import { ICONS } from '../icons/index.js';

/**
 * **`UnderstandingPanel`**（D11 / §4.3 / §6）。
 * **全 Requirement を名前付きで見せる。**
 *
 * ## INV-5 を型で守る（§6）
 *
 * この部品の props は **`id` / `displayLabel` / `status` しか受け取らない**。
 * **`statement` / `checkCriteria` / `sealedSolution` を渡せない型**にしてある。
 *
 * **理由は「渡さなければよい」では足りないから**である —
 * 型が許すと、いつか誰かが「デバッグに便利だから」渡す。
 * **T3 が `strength` を返さない**（D86 決定4）のと同じ流儀で、
 * **持てないものは型に現れない。**
 *
 * ## 並びを UI で作らない（D121 / §6）
 *
 * **`conceptKey` 昇順で engine が並べ、UI は sort しない。**
 *
 * 実データ 3 問で確かめた結果、**危険なのは level 順でもエッジでもなく
 * 「T2 が返す配列順が推論の順になる」ことだった**（3 問すべてで該当）。
 * **`status` 順にもしない** — 状態が変わるたびに行が動き、
 * §2.8 の「一覧を並べ替えない」に反する。
 *
 * **`conceptKey` は名前空間を持つので、昇順に並べると話題でまとまる。**
 *
 * ## 色だけで状態を伝えない
 *
 * **色 + 形の二重符号**（§5.5）。3 状態に 3 形（●／▲／○）を割り当て、
 * **読み上げにも状態名を出す**。
 */
export type RequirementStatus = 'sufficient' | 'insufficient' | 'unknown';

/**
 * **この形しか受け取らない。**
 * `statement` を持つオブジェクトを渡すと**型エラーになる**（余剰プロパティ検査）。
 */
export interface PanelRequirement {
  readonly id: string;
  readonly displayLabel: string;
  readonly status: RequirementStatus;
}

export interface UnderstandingPanelProps {
  /** **engine が `conceptKey` 昇順で並べたもの**（UI は並べ替えない = D121） */
  readonly requirements: readonly PanelRequirement[];
}

/**
 * **色 + 形 + 文字の三重符号**（§5.5）。
 *
 * **形は記号ではなくアイコンにした**（M16。実機で
 * 「**ステータスが○の中で表現されているのかが分からない**」と読まれた）。
 * **`●` と `○` は輪郭がほぼ同じ**で、**小さい字では 3 状態が 1 つに見える**。
 *
 * **`unknown` にアイコンを与えない**のは意図である —
 * **「まだ何も起きていない」を絵で語らない**（点線の輪だけを置く）。
 * **絵があると「確認済みで問題なし」と読まれうる。**
 */
const MARK: Readonly<
  Record<RequirementStatus, { readonly icon: string | undefined; readonly name: string }>
> = {
  sufficient: { icon: ICONS.complete, name: '理解できている' },
  insufficient: { icon: ICONS.attention, name: 'まだ確認中' },
  unknown: { icon: undefined, name: '未確認' },
};

export function UnderstandingPanel({ requirements }: UnderstandingPanelProps): ReactNode {
  return (
    <section className={styles['panel']} aria-label="理解の状態">
      <h2 className={styles['heading']}>理解の状態</h2>
      {/**
       * **件数だけ出す**（モック）。**Graph の版は出さない** —
       * `Graph` は内部語であり `writing-guidelines.md` §5 がゲートで禁じている。
       */}
      {requirements.length === 0 ? null : (
        <p className={styles['sub']}>{requirements.length} 件</p>
      )}
      {requirements.length === 0 ? (
        /**
         * **空状態**（§5.5 の 3 状態のうち「空」）。
         * **不可視フェーズの間はここに何も無い**（§4.4: 10〜55 秒は何も出さない）。
         * **「準備中」と書かない** — 書くと不可視フェーズの存在が漏れる。
         */
        <p className={styles['empty']}>問題を読み取ると、ここに確認する項目が並びます。</p>
      ) : (
        <ul className={styles['list']}>
          {requirements.map((r) => (
            <li key={r.id} className={[styles['row'], styles[r.status]].join(' ')}>
              <span className={styles['mark']} aria-hidden="true">
                {MARK[r.status].icon === undefined ? null : (
                  <Icon svg={MARK[r.status].icon as string} size={16} />
                )}
              </span>
              <span className={styles['label']}>
                {r.displayLabel}
                {/**
                 * **状態を文字で出す**（モック）。**色が読めなくても意味が届く**。
                 * **読み上げにも同じ語が届く**ので `VisuallyHidden` は要らなくなった。
                 */}
                <span className={styles['status']}>{MARK[r.status].name}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
